import type { AxiosInstance } from 'axios';
import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@libs/api/types';
import { tokenRefreshManager } from '../token/refresh';
import { LOGIN_REQUIRED_ERROR_CODES } from '@constants/error-codes';
import { useAuthStore } from '@stores/auth-store';
import * as ToastUtil from '@utils/toast-utils';
import { getErrorMessage } from '@utils/error-utils';
import { isAxiosError } from 'axios';

/**
 * 응답 인터셉터 설정
 */
export const setupResponseInterceptor = (apiClient: AxiosInstance) => {
  return {
    onFulfilled: (response: AxiosResponse<ApiResponse<unknown>>) => {
      const data = response.data;

      if (import.meta.env.DEV) {
        console.debug('🟢 [API Interceptor] 응답 성공:', {
          status: response.status,
          url: response.config.url,
          message: data?.message,
          hasData: !!data?.data,
        });
      }

      // TOKEN_REFRESHED 메시지 처리
      if (response.status === 200 && data?.message === 'TOKEN_REFRESHED') {
        const originalRequest = response.config as InternalAxiosRequestConfig & {
          _isRetrying?: boolean;
        };

        if (originalRequest._isRetrying) {
          return Promise.reject(new Error('Prevent duplicate retries'));
        }

        originalRequest._isRetrying = true;
        return apiClient.request(originalRequest);
      }

      return response;
    },

    onRejected: async (error: AxiosError<ApiResponse<unknown>>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _isRetrying?: boolean;
      };

      if (import.meta.env.DEV) {
        console.debug('🔴 [API Interceptor] 에러 발생:', {
          status: error.response?.status,
          url: originalRequest?.url,
          method: originalRequest?.method,
          isRetrying: originalRequest?._isRetrying,
          isRefreshing: tokenRefreshManager.getIsRefreshing(),
          hasCookies: document.cookie.length > 0,
          cookieInfo: document.cookie.length > 0 ? document.cookie.split(';').map((c) => c.trim().split('=')[0]) : [],
        });
      }

      // 401 에러 처리
      if (error.response?.status === 401) {
        const errorData = error.response.data;
        const errorCode = errorData?.error?.code;

        if (import.meta.env.DEV) {
          console.debug('🔴 [401 에러] 인증 에러 발생:', {
            errorCode,
            errorMessage: errorData?.error?.message,
            errorData: errorData,
          });
        }

        // 리프레시 불가능한 에러 코드 처리
        if (
          errorCode &&
          LOGIN_REQUIRED_ERROR_CODES.includes(errorCode as (typeof LOGIN_REQUIRED_ERROR_CODES)[number])
        ) {
          if (import.meta.env.DEV) {
            console.log('🔴 [loginRequiredErrorCodes] 에러 코드 매칭됨:', errorCode);
            console.log('🔴 [loginRequiredErrorCodes] 로그아웃 처리 시작');
          }

          useAuthStore.getState().signOut();

          if (!tokenRefreshManager.getHasShownExpirationAlert()) {
            tokenRefreshManager.resetExpirationAlert(); // reset 후 즉시 사용
            if (import.meta.env.DEV) {
              console.log('🔴 [loginRequiredErrorCodes] alert 표시 및 로그인 페이지로 이동');
            }
            ToastUtil.warning('로그인 정보가 만료되어 로그인 페이지로 이동합니다.');
            const currentPath = window.location.pathname + window.location.search;
            const returnToParam = currentPath !== '/' ? `?returnTo=${encodeURIComponent(currentPath)}` : '';
            window.location.href = `/sign-in${returnToParam}`;
          }

          // 이미 토스트를 표시했으므로 추가 토스트는 표시하지 않음
          return Promise.reject(new Error(error.message));
        }

        // 리프레시 가능한 에러 처리
        try {
          return await tokenRefreshManager.handle401Error(error, originalRequest);
        } catch (refreshError) {
          if (import.meta.env.DEV) {
            console.error('🔵 [401 에러] 기타 인증 에러:', errorData?.error?.message);
          }
          // 리프레시 실패 시에도 에러 메시지 표시하지 않음 (인증 관련은 이미 처리됨)
          return Promise.reject(refreshError);
        }
      }

      // 일반 에러 처리: 에러 메시지를 토스트로 표시
      if (isAxiosError(error) && error.response) {
        const errorMessage = getErrorMessage(error);
        if (errorMessage) {
          ToastUtil.error(errorMessage);
        }
      }

      return Promise.reject(error);
    },
  };
};
